import React from 'react';
import { useTranslation } from 'next-i18next';

import {
  Form, Formik
} from 'formik';
import * as yup from 'yup';
import classnames from 'classnames';

import * as Notifications from 'common/Notification';
import { FormGroup, Input } from 'components/ui';
import { ERROR_CODES } from 'constants/errors';
import { PHONE_NUMBER_RO_REGEX, handleValidationError } from 'helpers/FormHelpers';
import { addRoPrefix } from 'helpers/PhoneNumberHelpers';
import { useAccountMutation } from 'hooks/data/useAccountMutation';

import type { FormikHelpers, FormikProps } from 'formik';

import styles from './PhoneNumberForm.module.scss';

interface Props {
  className?: string,
  actionsClassName?: string,
  inputClassName?: string,
  inputWrapperClassName?: string,
  initialValues?: Partial<FormValues>,
  title?: string,
  placeholder?: string,
  onSubmitSuccess?: (number: string) => void,
  onSubmitError?: () => void,
  renderActions: (formikProps: FormikProps<Partial<FormValues>>) => JSX.Element,
  renderBeforeActions?: () => JSX.Element
}

interface FormValues {
  phoneNumber: string,
  prefix: string
}

const PhoneNumberForm = (props: Props) => {
  const {
    className,
    actionsClassName,
    inputClassName,
    inputWrapperClassName,
    initialValues = { phoneNumber: '' },
    title,
    placeholder,
    onSubmitError, onSubmitSuccess,
    renderBeforeActions,
    renderActions
  } = props;

  const { t } = useTranslation();
  const { sendPhoneVerificationCode } = useAccountMutation();

  const validationSchema = React.useMemo(() => yup.object().shape({
    phoneNumber: yup
      .string()
      .required(t('VALIDATION_ERRORS.REQUIRED'))
      .matches(PHONE_NUMBER_RO_REGEX, t('VALIDATION_ERRORS.INVALID_PHONE'))
  }), [t]);

  const handleSubmit = (values: FormValues, actions: FormikHelpers<FormValues>) => {
    const { setSubmitting } = actions;
    const { phoneNumber } = values;
    sendPhoneVerificationCode({
      // Automatically add hardcoded prefix for now
      data: { phoneNumber: addRoPrefix(phoneNumber) || '' },
      onSuccess: () => {
        if (onSubmitSuccess) {
          onSubmitSuccess(phoneNumber);
          setSubmitting(false);
        }
      },
      onError: (err) => {
        if (err?.response?.status === ERROR_CODES.VALIDATION_ERROR) {
          handleValidationError(err?.response?.data, actions);
        } else {
          Notifications.showError(t('ERRORS.DEFAULT_TOAST'), { autoClose: 2000, toastId: 'phoneCodeVerificationError' });
        }
        if (onSubmitError) {
          onSubmitError();
        }
        setSubmitting(false);
      }
    });
  };

  return (
    <div className={classnames(styles.root, className)}>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnChange
        validateOnBlur
        onSubmit={handleSubmit}
      >
        {(formikProps) => {
          return (
            <Form className={styles.form}>
              <FormGroup
                inline
                withPadding={false}
                title={title}
                titleClassName={styles.title}
                fieldsClassName={styles.fields}
              >
                {/* Enable when we need prefix support */}
                {/* <Select
                  className={classnames(styles.select, selectClassName)}
                  options={OPTIONS}
                  name="prefix"
                  form={formikProps}
                  disabled
                /> */}
                <Input
                  /* @ts-ignore  */
                  name="phoneNumber"
                  className={classnames(styles.inputWrapper, inputWrapperClassName)}
                  inputClassName={classnames(styles.input, inputClassName)}
                  placeholder={placeholder || t('PHONE_VALIDATION.FORM_STEP.NUMBER_PLACEHOLDER')}
                  autoComplete="tel"
                  type="tel"
                />
              </FormGroup>
              {renderBeforeActions ? renderBeforeActions() : null}
              <div className={classnames(styles.actions, actionsClassName)}>
                {renderActions(formikProps)}
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default PhoneNumberForm;
